import { Injectable } from '@angular/core';
import { interval, merge, Subject } from 'rxjs';
import { ConnectionStatusService } from '../connection-status.service';
import { TourInstallerSubmitRegisteredActionsControllerRequestService } from './controller-request-services/tour-installer-submit-registered-actions-controller-request.service';
import { TourInstallerSubmitAssetControllerRequestService } from './controller-request-services/tour-installer-submit-asset-controller-request.service';
import {
  Response,
  TourInstallerSubmitSubProjectControllerRequestService,
} from './controller-request-services/tour-installer-submit-sub-project-controller-request.service';
import { TourInstallerResetNotExecutedControllerRequestService } from './controller-request-services/tour-installer-reset-not-executed-controller-request.service';
import { TourInstallerDeleteAssetsControllerRequestService } from './controller-request-services/tour-installer-delete-assets-controller-request.service';
import {
  ImmoIPPlacerSubmitQueueService,
  ImmoIPSubmitType,
} from './immo-ip-placer-submit-queue.service';
import { ImmoIPPlacerSubmitInProgressService } from './immo-ip-placer-submit-in-progress.service';
import { ImmoIPSubProjectChangesObject } from '../immo-ip-placer-offline-changes/immo-ip-sub-project-changes/immo-ip-sub-project-changes-object';
import ErrorHelper from 'src/app/helpers/error-helper';
import { SnackService } from '../snack.service';
import {
  ChangeType,
  OfflineStorageTourService,
} from 'src/app/pages/immo-ip/placer/tour-view/offline-storage/offline-storage-tour.service';
import { ImmoIPProductChangesObject } from '../immo-ip-placer-offline-changes/immo-ip-product-changes/immo-ip-product-changes-object';
import {
  SUB_PROJECT_TYPES,
  SubProject,
} from 'src/app/model/project/sub-project';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { ImmoIPProductChangesService } from '../immo-ip-placer-offline-changes/immo-ip-product-changes/immo-ip-product-changes.service';
import { OfflineAsset } from '../placer-offline-changes/offline-asset';
import { ImmoIPSubProjectChangesService } from '../immo-ip-placer-offline-changes/immo-ip-sub-project-changes/immo-ip-sub-project-changes.service';
import ProductAsset from 'src/app/model/product-asset';
import { ImmoIPPlacerSubmitHelper } from './helper/immo-ip-placer-submit-helper';
import { ImmoIPNotExecutedHistoryItemObject } from '../immo-ip-placer-offline-changes/immo-ip-sub-project-changes/immo-ip-not-executed-history-item-object';
import { TourInstallerResetExecutedControllerRequestService } from './controller-request-services/tour-installer-reset-executed-controller-request.service';

@Injectable({
  providedIn: 'root',
})
export class ImmoIPPlacerSubmitService {
  private isSubmitting: boolean = false;
  private online: boolean = true;
  private readonly trySubmitInterval = 1000 * 10; // 10 seconds
  private connectionRestoredSubject = new Subject<any>();

  constructor(
    private connectionStatusService: ConnectionStatusService,
    private tourInstallerSubmitRegisteredActionsControllerRequestService: TourInstallerSubmitRegisteredActionsControllerRequestService,
    private tourInstallerSubmitAssetControllerRequestService: TourInstallerSubmitAssetControllerRequestService,
    private tourInstallerSubmitSubProjectControllerRequestService: TourInstallerSubmitSubProjectControllerRequestService,
    private tourInstallerResetExecutedControllerRequestService: TourInstallerResetExecutedControllerRequestService,
    private tourInstallerResetNotExecutedControllerRequestService: TourInstallerResetNotExecutedControllerRequestService,
    private tourInstallerDeleteAssetsControllerRequestService: TourInstallerDeleteAssetsControllerRequestService,
    private offlineStorageTourService: OfflineStorageTourService,
    private immoIPSubProjectChangesService: ImmoIPSubProjectChangesService,
    private immoIPProductChangesService: ImmoIPProductChangesService,
    private immoIPPlacerSubmitQueueService: ImmoIPPlacerSubmitQueueService,
    private immoIPPlacerSubmitInProgressService: ImmoIPPlacerSubmitInProgressService,
    private snackService: SnackService
  ) {
    this.connectionStatusService
      .getConnectionStatusSubject()
      .subscribe((online: boolean) => {
        const doSubmit = !this.online && online;
        this.online = online;
        if (doSubmit) {
          this.connectionRestoredSubject.next();
        }
      });
    merge(
      this.connectionRestoredSubject,
      interval(this.trySubmitInterval)
    ).subscribe(() => {
      this.doSubmit();
    });
  }

  private async doSubmit(): Promise<void> {
    if (this.isSubmitting || !this.online) {
      return;
    }
    this.isSubmitting = true;
    try {
      const toSubmit = await this.immoIPPlacerSubmitQueueService.getToSubmit();
      if (!CollectionHelper.isPopulated(toSubmit)) {
        return;
      }
      for (const submitEntry of toSubmit) {
        const subProjectChanges =
          await this.immoIPSubProjectChangesService.getObject(
            submitEntry.subProjectId
          );
        const isInProgress =
          await this.immoIPPlacerSubmitInProgressService.isInProgress(
            submitEntry.subProjectId
          );
        if (isInProgress) {
          continue;
        } else if (subProjectChanges == null) {
          await this.immoIPPlacerSubmitQueueService.removeToSubmit(
            submitEntry.subProjectId
          );
        } else {
          try {
            for (const submitType of submitEntry.submitTypes) {
              if (submitType === ImmoIPSubmitType.Full) {
                await this.doSubmitSubProject(
                  subProjectChanges,
                  submitEntry.dateString,
                  false
                );
              } else if (submitType === ImmoIPSubmitType.RegisteredActions) {
                await this.doSubmitRegisteredActions(
                  subProjectChanges,
                  submitEntry.dateString,
                  false
                );
              } else if (submitType === ImmoIPSubmitType.ResetNotExecuted) {
                await this.doReset(
                  subProjectChanges,
                  submitEntry.dateString,
                  false,
                  false
                );
              } else if (submitType === ImmoIPSubmitType.SaveExecutionAssets) {
                await this.directlySubmitOfflineAssets(
                  subProjectChanges,
                  submitEntry.dateString,
                  false
                );
              } else if (
                submitType === ImmoIPSubmitType.SaveNotExecutedAssets
              ) {
                await this.directlySubmitNotExecutedAssets(
                  subProjectChanges,
                  submitEntry.dateString,
                  false
                );
              } else if (submitType === ImmoIPSubmitType.DeleteAssets) {
                await this.doDeleteAssets(
                  subProjectChanges,
                  submitEntry.dateString,
                  false
                );
              } else if (submitType === ImmoIPSubmitType.ChangeInternalInfo) {
                await this.doSubmitInternalInfo(subProjectChanges, false);
              } else if (submitType === ImmoIPSubmitType.PlacementConfig) {
                await this.doSubmitPlacementConfig(
                  subProjectChanges,
                  submitEntry.dateString,
                  false
                );
              } else if (submitType === ImmoIPSubmitType.Address) {
                await this.doSubmitAddress(
                  subProjectChanges,
                  submitEntry.dateString,
                  false
                );
              }
              await this.immoIPPlacerSubmitQueueService.removeTypeFromSubmitEntry(
                subProjectChanges.id,
                submitType
              );
            }
            await this.immoIPPlacerSubmitQueueService.removeToSubmit(
              submitEntry.subProjectId
            );
          } catch (err) {
            if (!ErrorHelper.isConnectionLostError(err)) {
              this.snackService.showError(
                `Fout bij indienen subopdracht ${submitEntry.subProjectId}: ${err}`
              );
            }
          }
        }
      }
    } finally {
      this.isSubmitting = false;
    }
  }

  public async retrySubmit(
    subProjectId: number,
    dateString: string
  ): Promise<void> {
    if (!this.online) {
      this.snackService.showWarning(
        'Er is geen verbinding, gelieve opnieuw te proberen als de verbinding hersteld is'
      );
      return;
    }
    const isInProgress =
      await this.immoIPPlacerSubmitInProgressService.isInProgress(subProjectId);
    if (isInProgress) {
      return;
    }
    const subProjectChangeObject =
      await this.immoIPSubProjectChangesService.getObject(subProjectId);
    if (subProjectChangeObject == null) {
      await this.immoIPPlacerSubmitQueueService.removeToSubmit(subProjectId);
    } else {
      try {
        await this.doSubmitSubProject(
          subProjectChangeObject,
          dateString,
          false
        );
      } catch (err) {
        if (!ErrorHelper.isConnectionLostError(err)) {
          this.snackService.showError(
            `Fout bij indienen subopdracht ${subProjectId}: ${err}`
          );
        }
      }
    }
  }

  public submitSubProject(
    subProjectChanges: ImmoIPSubProjectChangesObject,
    dateString: string
  ): Promise<void> {
    if (this.online) {
      return this.doSubmitSubProject(subProjectChanges, dateString, true).catch(
        (err) => {
          if (ErrorHelper.isConnectionLostError(err)) {
            this.snackService.showWarning(
              'Server kon niet bereikt worden, opdracht zal ingediend worden zodra internetverbinding hersteld is'
            );
          }
          this.immoIPPlacerSubmitQueueService.addToSubmit(
            dateString,
            subProjectChanges.id,
            ImmoIPSubmitType.Full
          );
          throw err;
        }
      );
    } else {
      this.snackService.showWarning(
        'Server kon niet bereikt worden, opdracht zal ingediend worden zodra internetverbinding hersteld is'
      );
      this.immoIPPlacerSubmitQueueService.addToSubmit(
        dateString,
        subProjectChanges.id,
        ImmoIPSubmitType.Full
      );
      return Promise.reject({ status: 0 });
    }
  }

  private async doSubmitSubProject(
    subProjectChanges: ImmoIPSubProjectChangesObject,
    dateString: string,
    immediateSubmit = false
  ): Promise<void> {
    const storedSubProject =
      await this.offlineStorageTourService.getSubProjectFromOffline(
        dateString,
        subProjectChanges.id,
        subProjectChanges.projectId
      );
    if (storedSubProject == null) {
      await this.immoIPPlacerSubmitQueueService.removeToSubmit(
        subProjectChanges.id
      );
    }
    let productChangeObjects: ImmoIPProductChangesObject[] = null;
    if (storedSubProject.type === SUB_PROJECT_TYPES.BASE_PROJECT) {
      if (
        storedSubProject.project == null ||
        !CollectionHelper.isPopulated(storedSubProject.project.products)
      ) {
        return;
      }
      productChangeObjects = await this.immoIPProductChangesService.getObjects(
        storedSubProject.project.products.map((p) => p.id)
      );
    }
    await this.immoIPPlacerSubmitInProgressService.setSubmitInProgress(
      subProjectChanges.id
    );
    try {
      await this.submitOfflineAssets(
        dateString,
        subProjectChanges,
        productChangeObjects,
        immediateSubmit
      );
      // at this point, depending on if we're submitting a non-executed subproject, we either need to first submit
      // the NotExecutedHistoryItem and then upload the assets for that NotExecutedHistoryItem, or
      // the NotExecutedHistoryItem was successfully submitted, but connection was lost while uploading the assets
      let response: Response = null;
      let submitNotExecutedHistoryItemsAssetsResult: SubmitNotExecutedHistoryItemsAssetsResult =
        {
          success: true,
          connectionLostError: false,
        };
      if (subProjectChanges.isChange) {
        // at this point, we know it concerns a non-executed subproject and that the corresponding NotExecutedHistoryItem
        // does exist, or it concerns an executed subproject, which means the subproject cannot have been submitted yet
        response =
          await this.tourInstallerSubmitSubProjectControllerRequestService.submitChanges(
            productChangeObjects,
            subProjectChanges
          );
      } else if (
        subProjectChanges.notExecutedHistoryItemObject == null ||
        subProjectChanges.notExecutedHistoryItemObject.id == null
      ) {
        // at this point, we know it concerns a non-executed subproject and that the corresponding NotExecutedHistoryItem
        // does not exist yet, or it concerns an executed subproject, which means the subproject cannot have been submitted yet
        response =
          await this.tourInstallerSubmitSubProjectControllerRequestService.submitSubProject(
            productChangeObjects,
            subProjectChanges
          );
        subProjectChanges.registeredActions = response.registeredActions;
        if (
          response.notExecutedHistoryItemId != null &&
          subProjectChanges.notExecutedHistoryItemObject != null
        ) {
          subProjectChanges.notExecutedHistoryItemObject.id =
            response.notExecutedHistoryItemId;
        }
      }
      if (
        subProjectChanges.notExecutedHistoryItemObject != null &&
        CollectionHelper.isPopulated(
          subProjectChanges.notExecutedHistoryItemObject.offlineAssets
        )
      ) {
        submitNotExecutedHistoryItemsAssetsResult =
          await this.submitNotExecutedHistoryItemAssets(
            subProjectChanges,
            immediateSubmit
          );
      } else if (subProjectChanges.notExecutedHistoryItemObject != null) {
        await this.immoIPSubProjectChangesService.storeObject(
          subProjectChanges
        );
      }
      if (response != null) {
        this.offlineStorageTourService.saveSubProjectChangeAndEmit(
          dateString,
          subProjectChanges.id,
          subProjectChanges.projectId,
          ChangeType.SUBMIT,
          {
            subProjectStatus: response.subProjectStatus,
            subProjectChanges: subProjectChanges,
            productChangesArr: productChangeObjects,
          },
          ImmoIPPlacerSubmitHelper.updateSubProjectExecution
        );
      }
      await this.immoIPPlacerSubmitInProgressService.removeSubmitInProgress(
        subProjectChanges.id
      );
      if (
        !submitNotExecutedHistoryItemsAssetsResult.success &&
        submitNotExecutedHistoryItemsAssetsResult.connectionLostError &&
        immediateSubmit
      ) {
        this.immoIPPlacerSubmitQueueService.addToSubmit(
          dateString,
          subProjectChanges.id,
          ImmoIPSubmitType.Full
        );
      }
    } catch (err) {
      if (!ErrorHelper.isConnectionLostError(err) && !immediateSubmit) {
        this.snackService.showError(err);
        await this.removeLocalChanges(subProjectChanges, productChangeObjects);
      } else {
        if (ErrorHelper.isConnectionLostError(err) && immediateSubmit) {
          this.snackService.showWarning(
            'Server kon niet bereikt worden, opdracht zal ingediend worden zodra internetverbinding hersteld is'
          );
        } else if (immediateSubmit) {
          this.snackService.showError(err);
        }
      }
      await this.immoIPPlacerSubmitInProgressService.removeSubmitInProgress(
        subProjectChanges.id
      );
      throw err;
    }
  }

  public submitRegisteredActions(
    subProjectChanges: ImmoIPSubProjectChangesObject,
    dateString: string
  ): Promise<void> {
    if (this.online) {
      return this.doSubmitRegisteredActions(
        subProjectChanges,
        dateString,
        true
      ).catch((err) => {
        if (ErrorHelper.isConnectionLostError(err)) {
          this.immoIPPlacerSubmitQueueService.addToSubmit(
            dateString,
            subProjectChanges.id,
            ImmoIPSubmitType.RegisteredActions
          );
        }
        throw err;
      });
    } else {
      this.immoIPPlacerSubmitQueueService.addToSubmit(
        dateString,
        subProjectChanges.id,
        ImmoIPSubmitType.RegisteredActions
      );
      return Promise.reject({ status: 0 });
    }
  }

  private async doSubmitRegisteredActions(
    subProjectChanges: ImmoIPSubProjectChangesObject,
    dateString: string,
    immediateSubmit = false
  ): Promise<void> {
    if (!CollectionHelper.isPopulated(subProjectChanges.registeredActions)) {
      throw new Error('Er zijn geen lokale geregistreerde acties');
    }

    try {
      const submittedRegisteredActions =
        await this.tourInstallerSubmitRegisteredActionsControllerRequestService.submitRegisteredActions(
          subProjectChanges.id,
          subProjectChanges.registeredActions
        );
      subProjectChanges.registeredActions.length = 0;
      await this.immoIPSubProjectChangesService.storeObject(subProjectChanges);
      await this.offlineStorageTourService.saveSubProjectChangeAndEmit(
        dateString,
        subProjectChanges.id,
        subProjectChanges.projectId,
        ChangeType.REGISTERED_ACTIONS,
        submittedRegisteredActions,
        ImmoIPPlacerSubmitHelper.addRegisteredActions
      );
    } catch (err) {
      if (!ErrorHelper.isConnectionLostError(err) && !immediateSubmit) {
        this.snackService.showError(err);
        this.removeLocalChanges(subProjectChanges);
      }
      throw err;
    }
  }

  public directlySubmitOfflineAssets(
    subProjectChanges: ImmoIPSubProjectChangesObject,
    dateString: string,
    immediateSubmit = false
  ): Promise<void> {
    if (this.online) {
      return this.loadProductChangeObjectsIfApplicable(
        subProjectChanges,
        dateString
      ).then(
        async (productChangeObjects: ImmoIPProductChangesObject[]) => {
          try {
            return this.submitOfflineAssets(
              dateString,
              subProjectChanges,
              productChangeObjects,
              immediateSubmit
            );
          } catch (err) {
            if (ErrorHelper.isConnectionLostError(err)) {
              this.snackService.showWarning(
                'Server kon niet bereikt worden, foto zal ingediend worden zodra internetverbinding hersteld is'
              );
              this.immoIPPlacerSubmitQueueService.addToSubmit(
                dateString,
                subProjectChanges.id,
                ImmoIPSubmitType.SaveExecutionAssets
              );
            }
            throw err;
          }
        },
        () =>
          this.immoIPPlacerSubmitQueueService.removeTypeFromSubmitEntry(
            subProjectChanges.id,
            ImmoIPSubmitType.SaveExecutionAssets
          )
      );
    } else {
      this.snackService.showWarning(
        'Server kon niet bereikt worden, foto zal ingediend worden zodra internetverbinding hersteld is'
      );
      this.immoIPPlacerSubmitQueueService.addToSubmit(
        dateString,
        subProjectChanges.id,
        ImmoIPSubmitType.SaveExecutionAssets
      );
      return Promise.reject({ status: 0 });
    }
  }

  private async loadProductChangeObjectsIfApplicable(
    subProjectChanges: ImmoIPSubProjectChangesObject,
    dateString: string
  ): Promise<ImmoIPProductChangesObject[]> {
    const storedSubProject =
      await this.offlineStorageTourService.getSubProjectFromOffline(
        dateString,
        subProjectChanges.id,
        subProjectChanges.projectId
      );
    if (storedSubProject == null) {
      return null;
    }
    let toReturn: ImmoIPProductChangesObject[] = null;
    if (
      storedSubProject.type === SUB_PROJECT_TYPES.BASE_PROJECT &&
      storedSubProject.project != null &&
      CollectionHelper.isPopulated(storedSubProject.project.products)
    ) {
      toReturn = await this.immoIPProductChangesService.getObjects(
        storedSubProject.project.products.map((p) => p.id)
      );
    }
    return toReturn;
  }

  public directlySubmitNotExecutedAssets(
    subProjectChanges: ImmoIPSubProjectChangesObject,
    dateString: string,
    immediateSubmit = false
  ): Promise<void> {
    if (this.online) {
      return new Promise((resolve, reject) => {
        this.submitNotExecutedHistoryItemAssets(
          subProjectChanges,
          immediateSubmit
        )
          .then((res: SubmitNotExecutedHistoryItemsAssetsResult) => {
            if (res.success) {
              return resolve();
            } else {
              const err = res.connectionLostError
                ? { status: 0 }
                : new Error('submitNotExecutedHistoryItemAssets failed');
              return reject(err);
            }
          })
          .catch((err) => {
            if (ErrorHelper.isConnectionLostError(err)) {
              this.snackService.showWarning(
                'Server kon niet bereikt worden, foto zal ingediend worden zodra internetverbinding hersteld is'
              );
              this.immoIPPlacerSubmitQueueService.addToSubmit(
                dateString,
                subProjectChanges.id,
                ImmoIPSubmitType.SaveNotExecutedAssets
              );
            }
            return reject(err);
          });
      });
    } else {
      this.snackService.showWarning(
        'Server kon niet bereikt worden, foto zal ingediend worden zodra internetverbinding hersteld is'
      );
      this.immoIPPlacerSubmitQueueService.addToSubmit(
        dateString,
        subProjectChanges.id,
        ImmoIPSubmitType.SaveNotExecutedAssets
      );
      return Promise.reject({ status: 0 });
    }
  }

  private submitOfflineAssets(
    dateString: string,
    subProjectChanges: ImmoIPSubProjectChangesObject,
    productChangeObjects?: ImmoIPProductChangesObject[],
    immediateSubmit = false
  ): Promise<void> {
    return productChangeObjects != null
      ? this.submitOfflineProductAssets(
          dateString,
          subProjectChanges,
          productChangeObjects,
          immediateSubmit
        )
      : this.submitOfflineProjectPlacementAssets(
          dateString,
          subProjectChanges,
          immediateSubmit
        );
  }

  private async submitOfflineProductAssets(
    dateString: string,
    subProjectChangeObject: ImmoIPSubProjectChangesObject,
    productChangeObjects: ImmoIPProductChangesObject[],
    immediateSubmit: boolean
  ) {
    if (productChangeObjects.length === 0) {
      return;
    }
    for (const productChangeObject of productChangeObjects) {
      if (!CollectionHelper.isPopulated(productChangeObject.offlineAssets)) {
        continue;
      }
      const unsubmittedAssets = this.filterUnsubmittedAssets(
        productChangeObject.offlineAssets
      );
      if (!CollectionHelper.isPopulated(unsubmittedAssets)) {
        continue;
      }
      await this.submitMultipleOfflineAssets(
        true,
        dateString,
        unsubmittedAssets,
        productChangeObject,
        subProjectChangeObject,
        this.handleProductAssetResultMapBound,
        immediateSubmit
      );
    }
  }

  private async submitOfflineProjectPlacementAssets(
    dateString: string,
    subProjectChanges: ImmoIPSubProjectChangesObject,
    immediateSubmit: boolean
  ) {
    if (!CollectionHelper.isPopulated(subProjectChanges.offlineAssets)) {
      return;
    }
    const unsubmittedAssets = this.filterUnsubmittedAssets(
      subProjectChanges.offlineAssets
    );
    if (!CollectionHelper.isPopulated(unsubmittedAssets)) {
      return;
    }
    await this.submitMultipleOfflineAssets(
      false,
      dateString,
      unsubmittedAssets,
      null,
      subProjectChanges,
      this.handleProjectPlacementResultMapBound,
      immediateSubmit
    );
  }

  private filterUnsubmittedAssets(
    offlineAssets: OfflineAsset[]
  ): OfflineAsset[] {
    return offlineAssets == null
      ? null
      : offlineAssets.filter((a) => a.uuid == null);
  }

  private async submitMultipleOfflineAssets(
    baseProject: boolean,
    dateString: string,
    offlineAssets: OfflineAsset[],
    productChangeObject: ImmoIPProductChangesObject | null,
    subProjectChangeObject: ImmoIPSubProjectChangesObject | null,
    resultMapHandler: (
      dateString: string,
      m: Map<string, ProductAsset>,
      productChangeObject: ImmoIPProductChangesObject | null,
      subProjectChangeObject: ImmoIPSubProjectChangesObject | null
    ) => Promise<void>,
    immediateSubmit: boolean
  ): Promise<void> {
    const submitResult =
      await this.tourInstallerSubmitAssetControllerRequestService.submitMultipleOfflineAssets(
        offlineAssets,
        productChangeObject != null ? productChangeObject.id : null,
        !baseProject && subProjectChangeObject != null
          ? subProjectChangeObject.id
          : null,
        immediateSubmit
      );
    if (submitResult.resultMap.size > 0) {
      await resultMapHandler(
        dateString,
        submitResult.resultMap,
        productChangeObject,
        subProjectChangeObject
      );
    }
    if (submitResult.error != null) {
      if (immediateSubmit) {
        if (ErrorHelper.isConnectionLostError(submitResult.error)) {
          this.snackService.showWarning(
            'Server kon niet bereikt worden, opdracht zal ingediend worden zodra internetverbinding hersteld is'
          );
        } else {
          this.snackService.showError(submitResult.error);
        }
      }
      throw submitResult.error;
    }
  }

  private async handleProductAssetResultMap(
    dateString: string,
    resultMap: Map<string, ProductAsset>,
    productChangeObject: ImmoIPProductChangesObject,
    subProjectChangeObject: ImmoIPSubProjectChangesObject
  ): Promise<void> {
    productChangeObject.offlineAssets.forEach((o) => {
      const savedAsset = o.uuid == null ? resultMap.get(o.localFileUuid) : null;
      if (savedAsset != null) {
        o.uuid = savedAsset.asset.uuid;
      }
    });
    await this.immoIPProductChangesService.storeObject(productChangeObject);
    await this.offlineStorageTourService.saveSubProjectChangeAndEmit(
      dateString,
      subProjectChangeObject.id,
      subProjectChangeObject.projectId,
      ChangeType.ASSETS,
      {
        productId: productChangeObject.id,
        productAssets: [...resultMap.values()],
      },
      ImmoIPPlacerSubmitHelper.addProductAssets
    );
  }

  private handleProductAssetResultMapBound =
    this.handleProductAssetResultMap.bind(this);

  private async handleProjectPlacementResultMap(
    dateString: string,
    resultMap: Map<string, ProductAsset>,
    productChangeObject: ImmoIPProductChangesObject,
    subProjectChangeObject: ImmoIPSubProjectChangesObject
  ): Promise<void> {
    subProjectChangeObject.offlineAssets.forEach((o) => {
      const savedAsset = o.uuid == null ? resultMap.get(o.localFileUuid) : null;
      if (savedAsset != null) {
        o.uuid = savedAsset.asset.uuid;
      }
    });
    await this.immoIPSubProjectChangesService.storeObject(
      subProjectChangeObject
    );
    await this.offlineStorageTourService.saveSubProjectChangeAndEmit(
      dateString,
      subProjectChangeObject.id,
      subProjectChangeObject.projectId,
      ChangeType.ASSETS,
      [...resultMap.values()],
      ImmoIPPlacerSubmitHelper.addProjectPlacementAssets
    );
  }

  private handleProjectPlacementResultMapBound =
    this.handleProjectPlacementResultMap.bind(this);

  // return true only if all assets successfully submitted
  private async submitNotExecutedHistoryItemAssets(
    subProjectChangeObject: ImmoIPSubProjectChangesObject,
    immediateSubmit: boolean
  ): Promise<SubmitNotExecutedHistoryItemsAssetsResult> {
    const unsubmittedAssets = this.filterUnsubmittedAssets(
      subProjectChangeObject.notExecutedHistoryItemObject.offlineAssets
    );
    if (!CollectionHelper.isPopulated(unsubmittedAssets)) {
      return { success: true, connectionLostError: false };
    }
    try {
      await this.doSubmitNotExecutedHistoryItemAssets(
        unsubmittedAssets,
        subProjectChangeObject.notExecutedHistoryItemObject,
        immediateSubmit
      );
      await this.immoIPSubProjectChangesService.storeObject(
        subProjectChangeObject
      );
      return { success: true, connectionLostError: false };
    } catch (err) {
      if (!ErrorHelper.isConnectionLostError(err)) {
        this.snackService.showError(err);
        const offlineAssets = [
          ...subProjectChangeObject.notExecutedHistoryItemObject.offlineAssets,
        ];
        subProjectChangeObject.notExecutedHistoryItemObject.offlineAssets.length = 0;
        this.immoIPSubProjectChangesService.storeObject(subProjectChangeObject);
        this.immoIPSubProjectChangesService.cleanupNotExecutedHistoryItemAssets(
          offlineAssets
        );
        return { success: false, connectionLostError: false };
      } else {
        if (immediateSubmit) {
          this.snackService.showWarning(
            "Server kon niet bereikt worden, foto's zullen ingediend worden zodra internetverbinding hersteld is"
          );
        }
        return { success: false, connectionLostError: true };
      }
    }
  }

  private async doSubmitNotExecutedHistoryItemAssets(
    unsubmittedAssets: OfflineAsset[],
    notExecutedHistoryItemObject: ImmoIPNotExecutedHistoryItemObject,
    immediateSubmit: boolean
  ): Promise<void> {
    const submitResult =
      await this.tourInstallerSubmitAssetControllerRequestService.submitMultipleNotExecutedHistoryItemAssets(
        unsubmittedAssets,
        notExecutedHistoryItemObject.id,
        immediateSubmit
      );
    if (submitResult.resultMap.size > 0) {
      notExecutedHistoryItemObject.offlineAssets.forEach((o) => {
        const savedAsset =
          o.uuid == null ? submitResult.resultMap.get(o.localFileUuid) : null;
        if (savedAsset != null) {
          o.uuid = savedAsset.uuid;
        }
      });
    }
    if (submitResult.error != null) {
      throw submitResult.error;
    }
  }

  public reset(
    subProjectChanges: ImmoIPSubProjectChangesObject,
    dateString: string,
    executed: boolean
  ): Promise<void> {
    if (this.online) {
      return this.doReset(subProjectChanges, dateString, true, executed).catch(
        (err) => {
          if (ErrorHelper.isConnectionLostError(err)) {
            this.immoIPPlacerSubmitQueueService.addToSubmit(
              dateString,
              subProjectChanges.id,
              ImmoIPSubmitType.ResetNotExecuted
            );
          }
          throw err;
        }
      );
    } else {
      this.snackService.showWarning(
        'Server kon niet bereikt worden, opdracht zal ingediend worden zodra internetverbinding hersteld is'
      );
      this.immoIPPlacerSubmitQueueService.addToSubmit(
        dateString,
        subProjectChanges.id,
        ImmoIPSubmitType.ResetNotExecuted
      );
      return Promise.reject({ status: 0 });
    }
  }

  private async doReset(
    subProjectChanges: ImmoIPSubProjectChangesObject,
    dateString: string,
    immediateSubmit = false,
    executed: boolean
  ): Promise<void> {
    const storedSubProject =
      await this.offlineStorageTourService.getSubProjectFromOffline(
        dateString,
        subProjectChanges.id,
        subProjectChanges.projectId
      );
    if (storedSubProject == null) {
      await this.immoIPPlacerSubmitQueueService.removeToSubmit(
        subProjectChanges.id
      );
      return;
    }
    await this.immoIPPlacerSubmitInProgressService.setSubmitInProgress(
      subProjectChanges.id
    );
    try {
      if (executed) {
        await this.tourInstallerResetExecutedControllerRequestService.resetExecuted(
          subProjectChanges.id
        );
      } else {
        await this.tourInstallerResetNotExecutedControllerRequestService.resetNotExecuted(
          subProjectChanges.id
        );
      }
    } catch (err) {
      if (!ErrorHelper.isConnectionLostError(err) && !immediateSubmit) {
        this.snackService.showError(err);
      } else {
        if (ErrorHelper.isConnectionLostError(err) && immediateSubmit) {
          this.snackService.showWarning(
            'Server kon niet bereikt worden, opdracht zal ingediend worden zodra internetverbinding hersteld is'
          );
        } else if (immediateSubmit) {
          this.snackService.showError(err);
        }
      }
      throw err;
    } finally {
      this.immoIPPlacerSubmitInProgressService.removeSubmitInProgress(
        subProjectChanges.id
      );
    }
  }

  public deleteAssets(
    subProjectChanges: ImmoIPSubProjectChangesObject,
    dateString: string
  ): Promise<void> {
    if (this.online) {
      return this.doDeleteAssets(subProjectChanges, dateString, true).catch(
        (err) => {
          if (ErrorHelper.isConnectionLostError(err)) {
            this.immoIPPlacerSubmitQueueService.addToSubmit(
              dateString,
              subProjectChanges.id,
              ImmoIPSubmitType.DeleteAssets
            );
          }
          throw err;
        }
      );
    } else {
      this.snackService.showWarning(
        'Server kon niet bereikt worden, verwijdering van foto zal ingediend worden zodra internetverbinding hersteld is'
      );
      this.immoIPPlacerSubmitQueueService.addToSubmit(
        dateString,
        subProjectChanges.id,
        ImmoIPSubmitType.DeleteAssets
      );
      return Promise.reject({ status: 0 });
    }
  }

  private async doDeleteAssets(
    subProjectChanges: ImmoIPSubProjectChangesObject,
    dateString: string,
    immediateSubmit = false
  ): Promise<void> {
    const storedSubProject =
      await this.offlineStorageTourService.getSubProjectFromOffline(
        dateString,
        subProjectChanges.id,
        subProjectChanges.projectId
      );
    if (storedSubProject == null) {
      await this.immoIPPlacerSubmitQueueService.removeToSubmit(
        subProjectChanges.id
      );
      return;
    }
    await this.immoIPPlacerSubmitInProgressService.setSubmitInProgress(
      subProjectChanges.id
    );
    try {
      if (CollectionHelper.isPopulated(subProjectChanges.toDeleteAssetUuids)) {
        await this.tourInstallerDeleteAssetsControllerRequestService.deleteAssets(
          subProjectChanges.toDeleteAssetUuids
        );
        subProjectChanges.toDeleteAssetUuids.length = 0;
        await this.immoIPSubProjectChangesService.storeObject(
          subProjectChanges
        );
      }
    } catch (err) {
      if (!ErrorHelper.isConnectionLostError(err) && !immediateSubmit) {
        this.snackService.showError(err);
      } else {
        if (ErrorHelper.isConnectionLostError(err) && immediateSubmit) {
          this.snackService.showWarning(
            'Server kon niet bereikt worden, verwijdering van foto zal ingediend worden zodra internetverbinding hersteld is'
          );
        } else if (immediateSubmit) {
          this.snackService.showError(err);
        }
      }
      throw err;
    } finally {
      this.immoIPPlacerSubmitInProgressService.removeSubmitInProgress(
        subProjectChanges.id
      );
    }
  }

  public async submitInternalInfo(
    subProjectChanges: ImmoIPSubProjectChangesObject,
    dateString: string
  ): Promise<void> {
    if (this.online) {
      try {
        await this.doSubmitInternalInfo(subProjectChanges, true);
      } catch (err) {
        if (ErrorHelper.isConnectionLostError(err)) {
          this.immoIPPlacerSubmitQueueService.addToSubmit(
            dateString,
            subProjectChanges.id,
            ImmoIPSubmitType.ChangeInternalInfo
          );
        }
        throw err;
      }
    } else {
      this.immoIPPlacerSubmitQueueService.addToSubmit(
        dateString,
        subProjectChanges.id,
        ImmoIPSubmitType.ChangeInternalInfo
      );
      return Promise.reject({ status: 0 });
    }
  }

  private async doSubmitInternalInfo(
    subProjectChanges: ImmoIPSubProjectChangesObject,
    immediateSubmit = false
  ): Promise<void> {
    await this.immoIPPlacerSubmitInProgressService.setSubmitInProgress(
      subProjectChanges.id
    );
    try {
      await this.tourInstallerSubmitSubProjectControllerRequestService.submitInternalInfoChanges(
        subProjectChanges
      );
    } catch (err) {
      if (!ErrorHelper.isConnectionLostError(err) && !immediateSubmit) {
        this.snackService.showError(err);
        await this.removeLocalChanges(subProjectChanges);
      } else if (!ErrorHelper.isConnectionLostError(err) && immediateSubmit) {
        this.snackService.showError(err);
      }
    } finally {
      await this.immoIPPlacerSubmitInProgressService.removeSubmitInProgress(
        subProjectChanges.id
      );
    }
  }

  public async submitPlacementConfig(
    subProjectChanges: ImmoIPSubProjectChangesObject,
    dateString: string
  ): Promise<SubProject> {
    if (this.online) {
      try {
        return this.doSubmitPlacementConfig(
          subProjectChanges,
          dateString,
          true
        );
      } catch (err) {
        if (ErrorHelper.isConnectionLostError(err)) {
          this.immoIPPlacerSubmitQueueService.addToSubmit(
            dateString,
            subProjectChanges.id,
            ImmoIPSubmitType.PlacementConfig
          );
        }
        throw err;
      }
    } else {
      this.immoIPPlacerSubmitQueueService.addToSubmit(
        dateString,
        subProjectChanges.id,
        ImmoIPSubmitType.PlacementConfig
      );
      return Promise.reject({ status: 0 });
    }
  }

  public async submitAddress(
    subProjectChanges: ImmoIPSubProjectChangesObject,
    dateString: string
  ): Promise<SubProject> {
    if (this.online) {
      try {
        return this.doSubmitAddress(subProjectChanges, dateString, true);
      } catch (err) {
        if (ErrorHelper.isConnectionLostError(err)) {
          this.immoIPPlacerSubmitQueueService.addToSubmit(
            dateString,
            subProjectChanges.id,
            ImmoIPSubmitType.Address
          );
        }
        throw err;
      }
    } else {
      this.snackService.showWarning(
        'Server kon niet bereikt worden, adresaanpassing zal ingediend worden zodra internetverbinding hersteld is'
      );
      this.immoIPPlacerSubmitQueueService.addToSubmit(
        dateString,
        subProjectChanges.id,
        ImmoIPSubmitType.Address
      );
      return Promise.reject({ status: 0 });
    }
  }

  private async doSubmitPlacementConfig(
    subProjectChanges: ImmoIPSubProjectChangesObject,
    dateString: string,
    immediateSubmit = false
  ): Promise<SubProject> {
    await this.immoIPPlacerSubmitInProgressService.setSubmitInProgress(
      subProjectChanges.id
    );
    try {
      const subProject =
        await this.tourInstallerSubmitSubProjectControllerRequestService.submitModifyPlacementConfig(
          subProjectChanges
        );
      await this.offlineStorageTourService.saveSubProjectChangeAndEmit(
        dateString,
        subProjectChanges.id,
        subProjectChanges.projectId,
        ChangeType.MODIFY_CONFIG,
        subProject,
        ImmoIPPlacerSubmitHelper.replaceSubProjectFields
      );
      return subProject;
    } catch (err) {
      if (!ErrorHelper.isConnectionLostError(err) && !immediateSubmit) {
        this.snackService.showError(err);
        await this.removeLocalChanges(subProjectChanges);
      } else if (!ErrorHelper.isConnectionLostError(err) && immediateSubmit) {
        this.snackService.showError(err);
      }
    } finally {
      await this.immoIPPlacerSubmitInProgressService.removeSubmitInProgress(
        subProjectChanges.id
      );
    }
  }

  private async doSubmitAddress(
    subProjectChanges: ImmoIPSubProjectChangesObject,
    dateString: string,
    immediateSubmit = false
  ): Promise<SubProject> {
    await this.immoIPPlacerSubmitInProgressService.setSubmitInProgress(
      subProjectChanges.id
    );
    try {
      const subProject =
        await this.tourInstallerSubmitSubProjectControllerRequestService.submitModifyAddress(
          subProjectChanges
        );
      await this.offlineStorageTourService.saveSubProjectChangeAndEmit(
        dateString,
        subProjectChanges.id,
        subProjectChanges.projectId,
        ChangeType.MODIFY_CONFIG,
        subProject,
        ImmoIPPlacerSubmitHelper.replaceSubProjectFields
      );
      return subProject;
    } catch (err) {
      if (!ErrorHelper.isConnectionLostError(err) && !immediateSubmit) {
        this.snackService.showError(err);
        await this.removeLocalChanges(subProjectChanges);
      } else if (!ErrorHelper.isConnectionLostError(err) && immediateSubmit) {
        this.snackService.showError(err);
      } else if (ErrorHelper.isConnectionLostError(err) && immediateSubmit) {
        this.snackService.showWarning(
          'Server kon niet bereikt worden, adresaanpassing zal ingediend worden zodra internetverbinding hersteld is'
        );
      }
    } finally {
      await this.immoIPPlacerSubmitInProgressService.removeSubmitInProgress(
        subProjectChanges.id
      );
    }
  }

  private async removeLocalChanges(
    subProjectChanges: ImmoIPSubProjectChangesObject,
    productChangeObjects?: ImmoIPProductChangesObject[]
  ): Promise<void> {
    await this.immoIPPlacerSubmitQueueService.removeToSubmit(
      subProjectChanges.id
    );
    await this.immoIPSubProjectChangesService.removeObject(subProjectChanges);
    await this.immoIPProductChangesService.removeObjects(productChangeObjects);
  }
}

interface SubmitNotExecutedHistoryItemsAssetsResult {
  success: boolean;
  connectionLostError: boolean;
}
